import React from 'react';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useEffect } from 'react';
import checkBrowserSupport from 'shared/components/UnsupportedBrowserPopover/checkBrowserSupport';
import { BrowserSupportFeatures } from 'shared/enums/BrowserSupportFeatures';

interface ClipboardItem {
  readonly types: string[];
  readonly presentationStyle: 'unspecified' | 'inline' | 'attachment';
  getType(): Promise<Blob>;
}

interface ClipboardItemData {
  [mimeType: string]: Blob | string | Promise<Blob | string>;
}

declare const ClipboardItem: {
  prototype: ClipboardItem;
  new (itemData: ClipboardItemData): ClipboardItem;
};

interface ToastOptions {
  title: string;
  description: string;
  status: 'success' | 'error' | 'warning' | 'info';
}

const captureScreenshot = async (toast: (options: ToastOptions) => void): Promise<void> => {
  if (checkBrowserSupport(BrowserSupportFeatures.Screenshot).isBrowserSupported) {
    try {
      const date = new Date();
      let captureStream = null;

      // Until we update typescript to at least 4.4.0, CaptureController() is not recognized
      // eslint-disable-next-line
      const controller = new (window as any).CaptureController();
      controller.setFocusBehavior('no-focus-change');
      // Until we update typescript to at least 4.4.0, mediaDevices.getDisplayMedia() is not recognized
      // eslint-disable-next-line
      captureStream = await (navigator.mediaDevices as any).getDisplayMedia({
        controller,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        audio: false,
      });

      const tracks = captureStream?.getTracks();
      const videoTrack = tracks?.[0];

      // Until we update typescript to at least 4.4.0, window.ImageCapture() is not recognized
      // eslint-disable-next-line
      const imageCapture = new (window as any).ImageCapture(videoTrack);
      const frame = (await imageCapture.grabFrame()) as ImageBitmap;
      imageCapture.track.stop();
      const canvas = document.createElement('canvas');

      canvas.width = frame.width;
      canvas.height = frame.height;

      const ctx = canvas.getContext('bitmaprenderer');
      ctx?.transferFromImageBitmap(frame);
      const dataURL = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `Otto-screenshot-${date.toLocaleDateString()}-${date.toLocaleTimeString()}.png`;
      link.href = dataURL;
      link.click();
      canvas.toBlob((blob) => {
        if (blob) {
          try {
            // Until we update typescript to at least 4.4.0, clipboard.write is not recognized
            // eslint-disable-next-line
            (navigator.clipboard as any).write([
              new ClipboardItem({
                'image/png': blob,
              }),
            ]);
          } catch (error) {
            console.error(error);
          }
        }
      }, 'image/png');
    } catch (err) {
      toast({
        title: 'Unable to capture screen shot',
        description: 'Please try again. If the problem persists, please contact support.',
        status: 'error',
      });
      console.error('Unable to capture screen shot.');
      console.error(`Error: ${err}`);
    }
  } else {
    toast({
      title: 'Browser not supported for screenshots',
      description: 'Screenshots are not supported in your browser. Please use Chrome or Edge.',
      status: 'error',
    });
    console.log('Browser not supported for screenshots');
  }
};

const CaptureScreenshot = (): JSX.Element => {
  const toast = useToast();
  // Otto Screen shot
  // Ctrl + Shift + C

  useEffect(() => {
    const screenShot = (e: KeyboardEvent): void => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'C' || e.key === 'c')) {
        captureScreenshot(toast);
      }
    };
    window.addEventListener('keydown', screenShot);
    return (): void => window.removeEventListener('keydown', screenShot);
  }, [toast]);
  return <></>;
};

export default CaptureScreenshot;
